/**
 * Las tarjetas con las que el copiloto pide el sí cuando escribe la estructura de
 * la obra. Todo puro: mapea la `action` que devuelve /agent/chat a
 * lo que el panel dibuja. Los botones no llaman a nada propio: mandan como mensaje
 * el texto que trae la acción, igual que si la persona lo escribiera.
 */

import { nombreDeColeccion, textoDeMotivo } from 'src/components/controlObra/textosDeEstructura';

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

function tarjetaDeshacerPrevia(payload) {
  const r = payload.resumen;
  const solo = Array.isArray(r.alcance) && r.alcance.length ? `solo ${r.alcance.join(', ')}` : 'todo el lote';
  return {
    tipo: 'vista_previa',
    deshacer: true,
    titulo: r.lote ? `Deshacer: ${r.lote}` : 'Deshacer el último cambio',
    detalle: [r.fecha, solo, r.cambios != null ? plural(r.cambios, 'cambio', 'cambios') : null].filter(Boolean),
    rubros: [], movidas: [], rechazados: [],
    confirmar: payload.confirmar, cancelar: payload.cancelar,
  };
}

function tarjetaVistaPrevia(payload) {
  const r = payload?.resumen;
  if (!r || !payload.confirmar || !payload.cancelar) return null;
  if (payload.deshacer) return tarjetaDeshacerPrevia(payload);

  const esLote = Array.isArray(r.nombres);
  return {
    tipo: 'vista_previa',
    deshacer: false,
    titulo: esLote
      ? `${r.rubros} ${r.rubros === 1 ? 'rubro pasa' : 'rubros pasan'} a una sola fila`
      : plural(r.pasos ?? 0, 'cambio', 'cambios'),
    detalle: Array.isArray(r.detalle) ? r.detalle : [],
    rubros: esLote ? r.nombres : [],
    movidas: Object.entries(r.movidas || {}).map(([coleccion, cantidad]) => ({
      coleccion, etiqueta: nombreDeColeccion(coleccion), cantidad,
    })),
    rechazados: (r.rechazados || []).map((x) => ({ nombre: x.nombre, motivo: textoDeMotivo(x.motivo) || String(x.motivo || 'no se puede') })),
    totalesIguales: (r.absorciones || []).length > 0 && r.absorciones.every((a) => a.totales_iguales === true),
    confirmar: payload.confirmar,
    cancelar: payload.cancelar,
  };
}

function tarjetaDeshacer(payload) {
  if (!payload?.mensaje) return null;
  return { tipo: 'deshacer', loteId: payload.lote_id, resumen: payload.resumen || null, mensaje: payload.mensaje };
}

export function tarjetaDeAccion(accion) {
  if (!accion || typeof accion !== 'object') return null;
  switch (accion.type) {
    case 'control_obra_vista_previa': return tarjetaVistaPrevia(accion.payload);
    case 'control_obra_deshacer': return tarjetaDeshacer(accion.payload);
    default: return null;
  }
}

export function tarjetasDelTurno(acciones) {
  return (Array.isArray(acciones) ? acciones : []).map(tarjetaDeAccion).filter(Boolean);
}

/** El turno aplicó cambios en la obra: lo que la pantalla muestra quedó viejo. */
export function aplicoCambios(acciones) {
  return (Array.isArray(acciones) ? acciones : []).some((a) => ['control_obra_deshacer', 'control_obra_deshecho'].includes(a?.type));
}

/** Solo el último turno es una pregunta abierta; las anteriores ya se contestaron. */
export function esTarjetaVigente(hilo, turnoId, enviando) {
  if (enviando || !Array.isArray(hilo) || !hilo.length) return false;
  return hilo[hilo.length - 1].id === turnoId;
}

/**
 * Con un tema de la cola abierto, ¿el mensaje lo contesta a él o al chat? Si el último en
 * hablar fue el agente en esta sesión, la conversación está con él: mandarlo al intérprete
 * del tema leería «sí, preparala» o «sacá Plomería» como respuesta a la propuesta de la cola.
 */
export function vaAlTema({ hayTema, ultimo }) {
  if (!hayTema) return false;
  const ultimoFueElAgente = ultimo?.rol === 'assistant' && ultimo.delAgente && ultimo.enVivo;
  return !ultimoFueElAgente;
}
